<template>
  <ProductSettingsGenericFilter
    heading="GENERAL.COLORS"
    :count="filters.activeAppearanceGroups.length"
  >
    <div class="nested-filters">
      <ColorCircle
        v-for="group in allAppearanceGroups"
        :key="group.id"
        class="color"
        :size="24"
        :color="group.uiColor"
        :label="group.name"
        :name="group.name"
        :disable-tooltip="true"
        :active="isActive(group)"
        :deactivated="!isActive(group) && isDeactivated(group)"
        @on-click="updateColorFilter(group)"
      />
    </div>
  </ProductSettingsGenericFilter>
</template>

<script setup lang="ts">
import ColorCircle from 'commons/colorCircle/ColorCircle.vue';
import compositionCategoryHelper, {
  CompositionSpecificCategoryProductType,
  TranslatedAppearanceGroup,
} from 'commons/composition/compositionCategoryHelper.ts';
import { computed } from 'vue';
import {
  applyFilters,
  ProductTypeFilters,
} from 'src/app/partnerarea/publishing/detail/productSettings/advanced/filters/productTypeFilters.ts';
import ProductSettingsGenericFilter from './ProductSettingsGenericFilter.vue';

const { productTypes, filters } = defineProps<{
  productTypes: CompositionSpecificCategoryProductType[];
  filters: ProductTypeFilters;
}>();

const emits = defineEmits<{
  update: [activeAppearanceGroups: TranslatedAppearanceGroup[]];
}>();

const availableAppearanceGroupIds = computed<TranslatedAppearanceGroup[]>(() =>
  compositionCategoryHelper.getAllAvailableAppearanceGroups(
    applyFilters(productTypes, {
      ...filters,
      activeAppearanceGroups: [],
    }),
  ),
);

const allAppearanceGroups = computed<TranslatedAppearanceGroup[]>(() => {
  const available =
    compositionCategoryHelper.getAllAvailableAppearanceGroups(productTypes);

  // map to avoid duplicates
  const groupMap = new Map<string, TranslatedAppearanceGroup>();
  available.forEach((group) => groupMap.set(group.id, group));

  // add selected groups (even if not available in current product types)
  filters.activeAppearanceGroups.forEach((group) =>
    groupMap.set(group.id, group),
  );

  return Array.from(groupMap.values());
});

const isActive = ({ id }: TranslatedAppearanceGroup) =>
  filters.activeAppearanceGroups.some((group) => group.id === id);

const isDeactivated = ({ id }: TranslatedAppearanceGroup) =>
  !availableAppearanceGroupIds.value.some((group) => group.id === id);

const updateColorFilter = (group: TranslatedAppearanceGroup) => {
  const isCurrentlyActive = isActive(group);

  if (isCurrentlyActive) {
    emits(
      'update',
      filters.activeAppearanceGroups.filter((g) => g.id !== group.id),
    );
  } else {
    emits('update', [...filters.activeAppearanceGroups, group]);
  }
};
</script>

<style scoped lang="scss">
@import 'src/scss/styleguide/colors';

h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding-block: 8px;

  &.active {
    color: $pa-color-main;
  }
}

.nested-filters {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
  row-gap: 8px;

  .color {
    display: flex;
    flex-direction: row;
    gap: 4px;
    justify-content: flex-start;

    :deep(p) {
      color: $grey90;
      font-weight: normal;
    }
  }
}
</style>
